import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestoreIcon from '@mui/icons-material/Restore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useState } from 'react';
import type { ConversationEval } from 'services/types/conversationEvals';
import { AI_MODELS, DEFAULT_EVALUATION_AI_MODEL } from 'utils/aiModels';
import { DEFAULT_BLIND_COMPARISON_PROMPT, validatePromptTemplate } from 'utils/blindComparisonPrompts';

interface BlindComparisonFormProps {
  conversationEval: ConversationEval | null;
  onClose: () => void;
  onRunBlindComparison: (params: {
    models: string[];
    evaluationModel?: string;
    threshold?: number;
    businessUnitIds?: number[];
    customPrompt?: string;
  }) => void;
}

/**
 * Form component for configuring and running blind comparison evaluations
 * Allows selection of multiple models to compare and evaluation parameters
 */
const BlindComparisonForm: React.FC<BlindComparisonFormProps> = ({
  conversationEval,
  onClose,
  onRunBlindComparison,
}) => {
  const [formData, setFormData] = useState({
    models: [] as string[],
    evaluationModel: DEFAULT_EVALUATION_AI_MODEL,
    threshold: 0.8,
    customPrompt: DEFAULT_BLIND_COMPARISON_PROMPT,
  });

  const [promptValidation, setPromptValidation] = useState({ isValid: true, missingPlaceholders: [] as string[] });
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);
  const [isPromptCustomized, setIsPromptCustomized] = useState(false);

  const handlePromptChange = (newPrompt: string) => {
    const validation = validatePromptTemplate(newPrompt);
    setPromptValidation(validation);
    setFormData({ ...formData, customPrompt: newPrompt });
    setIsPromptCustomized(newPrompt !== DEFAULT_BLIND_COMPARISON_PROMPT);
  };

  /**
   * Resets prompt to default value
   */
  const handleResetPrompt = () => {
    setFormData({ ...formData, customPrompt: DEFAULT_BLIND_COMPARISON_PROMPT });
    setIsPromptCustomized(false);
    setPromptValidation({ isValid: true, missingPlaceholders: [] });
  };

  /**
   * Handles form submission and triggers blind comparison execution
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.models.length < 2) {
      alert('Debes seleccionar al menos 2 modelos para comparar');
      return;
    }
    if (!promptValidation.isValid) {
      alert(`El prompt debe contener los siguientes placeholders: ${promptValidation.missingPlaceholders.join(', ')}`);
      return;
    }
    onRunBlindComparison({
      ...formData,
      businessUnitIds: conversationEval?.businessUnitIds,
    });
  };

  /**
   * Handles multiple model selection from dropdown
   */
  const handleModelChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setFormData({
      ...formData,
      models: typeof value === 'string' ? value.split(',') : value,
    });
  };

  if (!conversationEval) {
    return null;
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ pt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Configuración de Comparación Ciega
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 3,
        }}
      >
        Evaluación #{conversationEval.id} - {conversationEval.messages.length} mensajes
        <br />
        Los modelos seleccionados generarán respuestas que serán comparadas de forma ciega.
      </Typography>
      <Grid container spacing={3}>
        <Grid size={12}>
          <FormControl fullWidth required>
            <InputLabel>Modelos a Comparar (mínimo 2)</InputLabel>
            <Select
              multiple
              value={formData.models}
              onChange={handleModelChange}
              input={<OutlinedInput label="Modelos a Comparar (mínimo 2)" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {AI_MODELS.map((modelName) => (
                <MenuItem key={modelName} value={modelName}>
                  {modelName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Modelo de Evaluación</InputLabel>
            <Select
              value={formData.evaluationModel}
              onChange={(e) => setFormData({ ...formData, evaluationModel: e.target.value })}
              label="Modelo de Evaluación"
            >
              {AI_MODELS.map((modelName) => (
                <MenuItem key={modelName} value={modelName}>
                  {modelName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Threshold de Éxito"
            type="number"
            value={formData.threshold}
            onChange={(e) => setFormData({ ...formData, threshold: parseFloat(e.target.value) })}
            helperText="Valor entre 0 y 1 para éxito"
            slotProps={{
              htmlInput: { min: 0, max: 1, step: 0.1 },
            }}
          />
        </Grid>

        <Grid size={12}>
          <Accordion
            expanded={isPromptExpanded}
            onChange={(_, expanded) => setIsPromptExpanded(expanded)}
            sx={{
              border: !promptValidation.isValid ? '1px solid #f44336' : '1px solid #e0e0e0',
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: isPromptCustomized ? '#f3e5f5' : '#f5f5f5',
                '& .MuiAccordionSummary-content': { alignItems: 'center' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                  Prompt de Evaluación
                  {isPromptCustomized && <Chip label="Personalizado" size="small" color="secondary" sx={{ ml: 2 }} />}
                </Typography>
                {isPromptCustomized && (
                  <Button
                    size="small"
                    startIcon={<RestoreIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResetPrompt();
                    }}
                    sx={{ mr: 1 }}
                  >
                    Restaurar
                  </Button>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 2,
                  }}
                >
                  {!promptValidation.isValid
                    ? `⚠️ Faltan placeholders requeridos: ${promptValidation.missingPlaceholders.join(', ')}`
                    : '✅ El prompt debe contener: {context}, {responseA}, {responseB}'}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={18}
                  value={formData.customPrompt}
                  onChange={(e) => handlePromptChange(e.target.value)}
                  error={!promptValidation.isValid}
                  variant="outlined"
                  sx={{
                    fontFamily: 'monospace',
                    '& .MuiInputBase-input': {
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      fontSize: '14px',
                      lineHeight: 1.6,
                    },
                    '& .MuiInputBase-root': {
                      maxHeight: '60vh',
                      overflow: 'auto',
                    },
                  }}
                  placeholder="Escribe tu prompt personalizado aquí..."
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 3,
        }}
      >
        <Button onClick={onClose} variant="outlined">
          Cancelar
        </Button>
        <Button type="submit" variant="contained" disabled={formData.models.length < 2 || !promptValidation.isValid}>
          Ejecutar Comparación Ciega
        </Button>
      </Box>
    </Box>
  );
};

export default BlindComparisonForm;
